import {
  hubServerMessageSchema,
  type HubClientMessage,
  type HubPresence,
  type HubServerMessage,
  type ProjectEvent,
  type ThreadActivity,
} from '@hub/contracts'
import { buildWsRoomPath, getWsBaseUrl } from '@/lib/collab/ws-url'

/**
 * Client for the ProjectHub socket (PLAN-081): presence, task drag hints and
 * project-wide change events. One instance per open project, owned by
 * `PresenceProvider`; feature hooks subscribe to the events they care about.
 */

export type HubStatus = 'connecting' | 'connected' | 'disconnected'

export interface ProjectHubEvents {
  presence: (presence: HubPresence[]) => void
  event: (event: ProjectEvent) => void
  activeThreads: (threads: ThreadActivity[]) => void
  status: (status: HubStatus) => void
}

const RECONNECT_BASE_MS = 500
const RECONNECT_MAX_MS = 10_000

export class ProjectHubClient {
  readonly projectId: string
  /** Everyone online except this connection. */
  presence: HubPresence[] = []
  activeThreads: ThreadActivity[] = []
  status: HubStatus = 'connecting'
  self: string | null = null

  #ws: WebSocket | null = null
  #closed = false
  #reconnectAttempt = 0
  #reconnectTimer: ReturnType<typeof setTimeout> | null = null
  #route: { currentRoute: string; currentRouteKey: string | null } | null = null
  #dragging: { taskId: string; fromCol: string } | null = null
  #listeners: { [K in keyof ProjectHubEvents]: Set<ProjectHubEvents[K]> } = {
    presence: new Set(),
    event: new Set(),
    activeThreads: new Set(),
    status: new Set(),
  }

  constructor(projectId: string) {
    this.projectId = projectId
  }

  on<K extends keyof ProjectHubEvents>(event: K, fn: ProjectHubEvents[K]): () => void {
    this.#listeners[event].add(fn)
    return () => this.#listeners[event].delete(fn)
  }

  #emit<K extends keyof ProjectHubEvents>(event: K, ...args: Parameters<ProjectHubEvents[K]>) {
    for (const fn of this.#listeners[event]) (fn as (...a: unknown[]) => void)(...args)
  }

  connect(): void {
    this.#closed = false
    this.#open()
  }

  #open() {
    if (this.#closed) return
    this.#setStatus('connecting')
    const url = `${getWsBaseUrl()}/${buildWsRoomPath(this.projectId, 'presence')}`
    const ws = new WebSocket(url)
    this.#ws = ws
    ws.onopen = () => {
      this.#reconnectAttempt = 0
      this.#setStatus('connected')
      // Re-announce where we are after a reconnect.
      if (this.#route) this.#sendRaw({ t: 'route', ...this.#route })
      if (this.#dragging) this.#sendRaw({ t: 'drag', dragging: this.#dragging })
    }
    ws.onmessage = (ev) => {
      let msg: HubServerMessage
      try {
        msg = hubServerMessageSchema.parse(JSON.parse(String(ev.data)))
      } catch (err) {
        console.error('[project-hub] bad server message', err)
        return
      }
      this.#handle(msg)
    }
    ws.onclose = () => {
      if (this.#ws === ws) this.#ws = null
      this.#setStatus('disconnected')
      this.#scheduleReconnect()
    }
    ws.onerror = () => {
      // onclose follows
    }
  }

  #scheduleReconnect() {
    if (this.#closed || this.#reconnectTimer) return
    const delay = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** this.#reconnectAttempt++)
    this.#reconnectTimer = setTimeout(() => {
      this.#reconnectTimer = null
      this.#open()
    }, delay)
  }

  close() {
    this.#closed = true
    if (this.#reconnectTimer) clearTimeout(this.#reconnectTimer)
    this.#reconnectTimer = null
    this.#ws?.close(1000, 'closed')
    this.#ws = null
  }

  #setStatus(status: HubStatus) {
    if (this.status === status) return
    this.status = status
    this.#emit('status', status)
  }

  #sendRaw(msg: HubClientMessage) {
    if (this.#ws?.readyState === WebSocket.OPEN) this.#ws.send(JSON.stringify(msg))
  }

  #handle(msg: HubServerMessage) {
    switch (msg.t) {
      case 'hello':
        this.self = msg.self
        this.#setPresence(msg.presence)
        this.activeThreads = msg.activeThreads
        this.#emit('activeThreads', msg.activeThreads)
        break
      case 'presence':
        this.#setPresence(msg.presence)
        break
      case 'event':
        if (msg.event.t === 'thread:activity') {
          const { threadId, state, updatedAt } = msg.event
          const rest = this.activeThreads.filter((a) => a.threadId !== threadId)
          this.activeThreads = [...rest, { threadId, state, updatedAt }]
          this.#emit('activeThreads', this.activeThreads)
        }
        this.#emit('event', msg.event)
        break
      case 'error':
        console.error('[project-hub]', msg.message)
        break
    }
  }

  #setPresence(presence: HubPresence[]) {
    this.presence = presence.filter((p) => p.connId !== this.self)
    this.#emit('presence', this.presence)
  }

  setRoute(currentRoute: string, currentRouteKey: string | null): void {
    this.#route = { currentRoute, currentRouteKey }
    this.#sendRaw({ t: 'route', currentRoute, currentRouteKey })
  }

  setDragging(dragging: { taskId: string; fromCol: string } | null): void {
    this.#dragging = dragging
    this.#sendRaw({ t: 'drag', dragging })
  }
}
